import { PlusCircleOutlined } from '@ant-design/icons'
import { Button, DatePicker, Empty, Menu, message } from 'antd'
import { unset } from 'lodash'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import AccessControlAction from '../../components/AccessControlAction'
import { EmptyText } from '../../components/Common/CellText'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import SpinnerInlineComponent from '../../components/SpinnerInlineComponent'
import { systemName } from '../../config'
import {
  ACL_ACTION_TYPE,
  DATE_FORMAT_DEFAULT,
  ISO_DATE_FORMAT,
  WARNING_MESSAGE,
} from '../../constants'
import date_utils from '../../date_utils'
import DashboardLayout from '../../layouts/DashboardLayout'
import utils from '../../utils'
import {
  buildImsatWorkScheduleCompanies,
  findSelectedImsatCompany,
  getImsatCpctmCodesToHide,
  getMergedLocalImsatConfig,
  getWorkScheduleViewPath,
  mergeWorkSchedules,
  shouldUseParentWorkScheduleView,
} from '../../utils/imsatWorkScheduleUtils'
import {
  BodyTableLeft,
  BodyTableRight,
  BodyTableWrapper,
  HeaderTableWrapper,
  ItemTableRight,
} from './CompanyWorkScheduleListPageStyled'

const renderTime = value => {
  return `${String(moment(value).hour()).padStart(2, '0')}h${String(
    moment(value).minute()
  ).padStart(2, '0')}`
}

const renderDate = value => {
  return `${String(moment(value).date()).padStart(2, '0')}/${String(
    moment(value).month() + 1
  ).padStart(2, '0')}`
}

const CompanyWorkScheduleListPage = props => {
  const {
    history,
    workScheduleStore,
    parentWorkScheduleStore,
    authenticationStore,
    companyStore,
    aclStore,
    infoOptionStore,
  } = props
  const { isPublic } = props

  const [workScheduleList, setWorkScheduledList] = useState([])
  const [isLoadingWorkSchedule, setIsLoadingWorkSchedule] = useState(false)
  const [days, setDays] = useState(date_utils.weekRange(date_utils.current()))

  const { currentUser } = authenticationStore
  const { companyList } = companyStore
  const { aclActionsByUser } = aclStore

  const { CODE: optionCode } = infoOptionStore.infoOptionList || {}

  const permissionViewList = toJS(workScheduleStore.workSchedulePermissionView)

  const [selectedKey, setSelectedKey] = useState(null)

  const imsatCompanies = buildImsatWorkScheduleCompanies(
    infoOptionStore.infoOptionList,
    companyList,
    permissionViewList,
    currentUser?.company?.code
  )
  const imsatCpctmCodesToHide = getImsatCpctmCodesToHide(imsatCompanies)
  const mergedLocalImsatConfig = getMergedLocalImsatConfig(
    infoOptionStore.infoOptionList,
    companyList,
    selectedKey,
    currentUser?.company?.code
  )
  const selectedImsatCompany = findSelectedImsatCompany(
    imsatCompanies,
    selectedKey
  )
  const isImsatCompanySelected = !!selectedImsatCompany
  const isParentCompanySelected =
    isImsatCompanySelected || selectedKey === optionCode

  const tableData = workScheduleList?.map(item => ({
    ...item,
    weekDay: moment(item.start_at).isoWeekday(),
  }))

  const convertTableData = [
    { weekday: 1, workSchedule: [] },
    { weekday: 2, workSchedule: [] },
    { weekday: 3, workSchedule: [] },
    { weekday: 4, workSchedule: [] },
    { weekday: 5, workSchedule: [] },
    { weekday: 6, workSchedule: [] },
    { weekday: 7, workSchedule: [] },
  ]

  tableData.forEach(item => {
    convertTableData[item.weekDay - 1].workSchedule.push(item)
  })

  const handleChangeWeek = useCallback(date => {
    setDays(date_utils.weekRange(date))
  }, [])

  const sum = convertTableData.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.workSchedule.length,
    0
  )

  const isAccessCreate =
    !isParentCompanySelected && selectedKey === currentUser?.company.code

  useEffect(() => {
    workScheduleStore.getWorkSchedulePermissionView()
  }, [])

  useEffect(() => {
    console.log('[WorkScheduleMenu] init selectedKey effect', {
      companyListLength: companyList?.length,
      currentUserCompanyCode: currentUser?.company?.code,
      currentUserCompanyCodeType: typeof currentUser?.company?.code,
    })

    if (currentUser?.company.code) {
      console.log(
        '[WorkScheduleMenu] setSelectedKey from currentUser',
        currentUser.company.code
      )
      setSelectedKey(currentUser.company.code)
    } else {
      console.warn(
        '[WorkScheduleMenu] không set selectedKey — thiếu currentUser.company.code'
      )
    }
  }, [companyList])

  useEffect(() => {
    if (aclActionsByUser && aclActionsByUser.length) {
      const isAccessWorkSchedule = aclActionsByUser.some(
        action =>
          action.code === ACL_ACTION_TYPE.menu__WORK_SCHEDULE &&
          action.status === true
      )
      if (!isAccessWorkSchedule) {
        message.error(WARNING_MESSAGE.ACCESS_PAGE, 3)
        history.push('/dashboard')
      }
    }
  }, [aclActionsByUser])

  useEffect(() => {
    ;(async () => {
      try {
        setIsLoadingWorkSchedule(true)
        const fromDate = moment(days[0]).format(ISO_DATE_FORMAT)
        const toDate = moment(days[6]).format(ISO_DATE_FORMAT)

        let data
        if (mergedLocalImsatConfig) {
          const localData = await workScheduleStore.getWorkSchedule(
            fromDate,
            toDate,
            true,
            selectedKey
          )

          let imsatData = []
          try {
            imsatData = await parentWorkScheduleStore.getWorkSchedule(
              fromDate,
              toDate,
              mergedLocalImsatConfig.imsatCode,
              mergedLocalImsatConfig.apiUrl
            )
          } catch (imsatError) {
            console.warn('Không lấy được lịch IMSAT cho công ty chính', imsatError)
          }

          data = mergeWorkSchedules(localData, imsatData)
        } else if (isImsatCompanySelected) {
          data = await parentWorkScheduleStore.getWorkSchedule(
            fromDate,
            toDate,
            selectedImsatCompany.code,
            selectedImsatCompany.apiUrl
          )
        } else if (isParentCompanySelected) {
          data = await parentWorkScheduleStore.getWorkSchedule(fromDate, toDate)
        } else if (isPublic) {
          data = await workScheduleStore.getPublicWorkSchedule(fromDate, toDate)
        } else {
          data = await workScheduleStore.getWorkSchedule(
            fromDate,
            toDate,
            true,
            selectedKey
          )
        }

        setWorkScheduledList(data)
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        setIsLoadingWorkSchedule(false)
      }
    })()

    return () => {
      workScheduleStore.clearWorkSchedule()
      parentWorkScheduleStore.clearWorkSchedule()
    }
  }, [
    days,
    selectedKey,
    isImsatCompanySelected,
    selectedImsatCompany?.code,
    mergedLocalImsatConfig?.imsatCode,
  ])

  const checkIsTodayWorkSchedule = date =>
    moment(date).format(DATE_FORMAT_DEFAULT) ===
    moment().format(DATE_FORMAT_DEFAULT)

  const changeCategory = e => {
    console.log('[WorkScheduleMenu] changeCategory click', {
      key: e.key,
      keyType: typeof e.key,
    })
    setSelectedKey(e.key)
  }

  const getScheduleViewPath = item =>
    getWorkScheduleViewPath(item, isParentCompanySelected)

  const menuSelectedKeys = [
    selectedImsatCompany?.code || selectedKey,
  ].filter(Boolean)

  useEffect(() => {
    console.log('[WorkScheduleMenu] state snapshot', {
      selectedKey,
      selectedKeyType: typeof selectedKey,
      selectedImsatCompanyCode: selectedImsatCompany?.code,
      selectedImsatCompanyCodeType: typeof selectedImsatCompany?.code,
      menuSelectedKeys,
      menuSelectedKeyTypes: menuSelectedKeys.map(k => typeof k),
      optionCode,
      isImsatCompanySelected,
      isParentCompanySelected,
      imsatCompanies: imsatCompanies.map(c => ({
        code: c.code,
        codeType: typeof c.code,
        name: c.name,
      })),
      permissionViewList: permissionViewList.map(p => p.access_type),
    })
  }, [
    selectedKey,
    selectedImsatCompany?.code,
    menuSelectedKeys,
    optionCode,
    isImsatCompanySelected,
    isParentCompanySelected,
    imsatCompanies,
    permissionViewList,
  ])

  const renderMenuItems = () => {
    let currentUserCompanyMenuItem = null
    const addedImsatCodes = new Set()
    const menuItemKeys = []

    const menuItems = companyList.map(company => {
      if (imsatCpctmCodesToHide.has(company.code)) {
        return null
      }

      const matchingPermission = permissionViewList.find(
        permission => permission.access_type === company.code
      )
      if (matchingPermission) {
        menuItemKeys.push(company.code)
        if (
          company.code === currentUser?.company.code &&
          !currentUserCompanyMenuItem
        ) {
          currentUserCompanyMenuItem = (
            <Menu.Item key={company.code}>{company.name}</Menu.Item>
          )
        } else {
          return <Menu.Item key={company.code}>{company.name}</Menu.Item>
        }
      }
      return null
    })

    if (currentUserCompanyMenuItem) {
      menuItems.unshift(currentUserCompanyMenuItem)
    }

    imsatCompanies.forEach(imsatCompany => {
      if (addedImsatCodes.has(imsatCompany.code)) {
        return
      }
      addedImsatCodes.add(imsatCompany.code)
      menuItemKeys.push(imsatCompany.code)
      menuItems.push(
        <Menu.Item key={imsatCompany.code}>{imsatCompany.name}</Menu.Item>
      )
    })

    const hasMatchingMenuKey = menuSelectedKeys.some(selected =>
      menuItemKeys.some(
        itemKey => String(itemKey) === String(selected)
      )
    )

    console.log('[WorkScheduleMenu] renderMenuItems', {
      menuItemKeys,
      menuItemKeyTypes: menuItemKeys.map(k => typeof k),
      menuSelectedKeys,
      hasMatchingMenuKey,
      mismatchHint: !hasMatchingMenuKey
        ? 'selectedKey không khớp bất kỳ Menu.Item key nào (có thể do type string/number)'
        : null,
    })

    return menuItems
  }

  const headerTable = (
    <HeaderTableWrapper>
      <span className={'date'}>Ngày tháng</span>
      <span className={'element'}>Nội dung công việc</span>
      <span className={'element'}>Chuẩn bị</span>
      <span className={'element'}>Thành viên tham gia</span>
      <span className={'element'}>Địa điểm</span>
      <span className={'element'}>Chủ trì</span>
    </HeaderTableWrapper>
  )

  return (
    <DashboardLayout title={'Link People - Link the World'}>
      <Helmet>
        <title>Lịch cơ quan | {systemName} Portal</title>
      </Helmet>
      <PageTitle location={props.location} title={'Lịch cơ quan'} hiddenGoBack>
        <div>
          <DatePicker
            onChange={handleChangeWeek}
            picker="week"
            placeholder={'Chọn tuần'}
            format={'Tuần w, YYYY'}
            defaultValue={moment()}
            style={{ height: 32, width: 200 }}
          />
          <AccessControlAction
            aclActionType={ACL_ACTION_TYPE.create__WORK_SCHEDULE}>
            <Button
              style={{ marginLeft: 15 }}
              onClick={() => history.push('/company-work-schedule/create')}
              type={'primary'}
              disabled={!isAccessCreate}>
              <PlusCircleOutlined />
              Tạo sự kiện mới
            </Button>
          </AccessControlAction>
        </div>
      </PageTitle>
      <div>
        <ContentBlockWrapper style={{ minHeight: 600 }}>
          <Menu
            mode="horizontal"
            selectedKeys={menuSelectedKeys}
            style={{ marginBottom: 16 }}
            onClick={changeCategory}>
            {renderMenuItems()}
          </Menu>
          {headerTable}
          {convertTableData.length > 0 && (
            <div style={{ marginBottom: -20, position: 'relative' }}>
              {convertTableData.map(el => {
                if (el.workSchedule.length === 0) return null
                return (
                  <div
                    key={el.weekday}
                    style={{
                      display: 'flex',
                      marginBottom: 20,
                      border: '1px solid #f0f0f0',
                    }}>
                    <BodyTableLeft
                      style={
                        checkIsTodayWorkSchedule(el.workSchedule[0].start_at)
                          ? {
                              backgroundColor: '#cddaf4',
                              borderLeft: '3px solid #2c65ac',
                            }
                          : { borderLeft: '1px solid #CDDAF4' }
                      }>
                      <div>{utils.renderWeekday(el.weekday)}</div>
                      <div>{renderDate(el.workSchedule[0].start_at)}</div>
                    </BodyTableLeft>
                    <div
                      style={{
                        flexGrow: 1,
                        backgroundColor: checkIsTodayWorkSchedule(
                          el.workSchedule[0].start_at
                        )
                          ? '#dee9fd'
                          : unset,
                      }}>
                      {el.workSchedule.map(item => (
                        <BodyTableWrapper key={item.schedule_code}>
                          <BodyTableRight>
                            <ItemTableRight>
                              <strong
                                style={{ fontWeight: 'bold', display: 'block' }}
                                onClick={() => history.push(getScheduleViewPath(item))}>
                                {renderTime(item.start_at)}
                                {item.end_at &&
                                  ` đến ${renderTime(item.end_at)}`}
                              </strong>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                                onClick={() => history.push(getScheduleViewPath(item))}>
                                {item.event_notice ? (
                                  utils.removeHtml(item.event_notice)
                                ) : (
                                  <EmptyText>Không có nội dung</EmptyText>
                                )}
                              </div>
                              {item.file_ids?.map(file => (
                                <FileItem
                                  key={file.file_id}
                                  file_id={file.file_id}
                                  file_name={file.file_title}
                                  isParentFile={shouldUseParentWorkScheduleView(
                                    item,
                                    isParentCompanySelected
                                  )}
                                  file_type={utils.getExtensionFile(
                                    file.file_title
                                  )}
                                />
                              ))}
                            </ItemTableRight>
                            <ItemTableRight
                              onClick={() => history.push(getScheduleViewPath(item))}>
                              {item.preparation ? (
                                item.preparation
                              ) : (
                                <EmptyText>Không có chuẩn bị.</EmptyText>
                              )}
                            </ItemTableRight>
                            <ItemTableRight
                              onClick={() => history.push(getScheduleViewPath(item))}>
                              {item.attenders ? (
                                item.attenders
                              ) : (
                                <EmptyText>
                                  Không có thành viên tham gia.
                                </EmptyText>
                              )}
                            </ItemTableRight>
                            <ItemTableRight
                              onClick={() => history.push(getScheduleViewPath(item))}>
                              {item.location ? (
                                item.location
                              ) : (
                                <EmptyText>Không có địa điểm.</EmptyText>
                              )}
                            </ItemTableRight>
                            <ItemTableRight
                              onClick={() => history.push(getScheduleViewPath(item))}>
                              {item.host ? (
                                item.host
                              ) : (
                                <EmptyText>Không có chủ trì.</EmptyText>
                              )}
                            </ItemTableRight>
                          </BodyTableRight>
                        </BodyTableWrapper>
                      ))}
                    </div>
                  </div>
                )
              })}
              <SpinnerInlineComponent
                isLoading={isLoadingWorkSchedule}
                sizeSpin={'small'}
                tip={'Đang tải lịch cơ quan'}
                alignItems
                noBackground
              />
            </div>
          )}
          {sum === 0 && !isLoadingWorkSchedule && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'Không có dữ liệu'}
              style={{ marginTop: 50 }}
            />
          )}
        </ContentBlockWrapper>
      </div>
    </DashboardLayout>
  )
}

export default memo(
  inject(
    'workScheduleStore',
    'parentWorkScheduleStore',
    'authenticationStore',
    'companyStore',
    'aclStore',
    'infoOptionStore'
  )(observer(CompanyWorkScheduleListPage))
)
